import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, qs, type ChannelStatus, type Paged } from '../api';
import { Badge, Button, Card, ErrorMessage, Field, Input, Notice, Pagination, Select, Spinner, number, unitMoney, when } from '../ui';

interface Listing {
  id: number;
  itemType: string;
  itemNo: string;
  itemName: string | null;
  colorName: string | null;
  condition: string;
  remoteQty: number;
  remotePrice: string | null;
  state: 'matched' | 'drift' | 'unmatched';
  lotId: number | null;
  localQty: number | null;
  localPrice: string | null;
}

const STATE_TONE = { matched: 'green', drift: 'amber', unmatched: 'blue' } as const;
const STATE_LABEL = { matched: 'Matches', drift: 'Differs', unmatched: 'Not in BrickLoop' } as const;

/**
 * Brick Owl: connect with an API key, test it, and read what is listed. Read-only. Nothing here can change your Brick
 * Owl store, and there is no switch for writing.
 */
export function BrickowlCard({ status, owner }: { status: ChannelStatus; owner: boolean }) {
  const qc = useQueryClient();
  const refresh = () => {
    for (const k of ['channels', 'listings-brickowl', 'item-marketplaces']) void qc.invalidateQueries({ queryKey: [k] });
  };
  const [apiKey, setApiKey] = useState('');
  const [mode, setMode] = useState<'off' | 'read_only'>(status.mode === 'off' ? 'off' : 'read_only');

  const save = useMutation({
    mutationFn: () => api('/api/v1/channels/brickowl', { method: 'PUT', body: { mode, ...(apiKey ? { credentials: { apiKey } } : {}) } }),
    onSuccess: () => {
      setApiKey('');
      refresh();
    },
  });
  const test = useMutation({ mutationFn: () => api<{ detail: string }>('/api/v1/channels/brickowl/test', { method: 'POST', body: {} }), onSuccess: refresh });
  const pull = useMutation({
    mutationFn: () => api<{ result: { fetched: number; skipped: { remoteId: string; reason: string }[] } }>('/api/v1/channels/brickowl/pull', { method: 'POST', body: {} }),
    onSuccess: refresh,
  });

  return (
    <>
      <Card
        title={
          <span className="flex items-center gap-2">
            Brick Owl {status.mode === 'off' ? <Badge>Off</Badge> : <Badge tone="blue">Read only</Badge>}
            {status.configured ? <Badge tone="green">Key saved</Badge> : <Badge tone="amber">No key</Badge>}
          </span>
        }
        actions={
          status.configured && owner ? (
            <>
              <Button variant="secondary" small disabled={test.isPending} onClick={() => test.mutate()}>
                {test.isPending ? 'Testing…' : 'Test connection'}
              </Button>
              <Button small disabled={status.mode === 'off' || pull.isPending} title={status.mode === 'off' ? 'Turn the connection on first' : undefined} onClick={() => pull.mutate()}>
                {pull.isPending ? 'Reading…' : 'Read inventory now'}
              </Button>
            </>
          ) : undefined
        }
      >
        <div className="space-y-4">
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-slate-500">Last read</dt>
              <dd>{when(status.lastPullAt)}</dd>
            </div>
            <div>
              <dt className="text-slate-500">Requests today</dt>
              <dd>
                {number(status.requestsToday.used)} of {number(status.requestsToday.cap)}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500">Result</dt>
              <dd className="truncate" title={status.lastPullStatus ?? undefined}>
                {status.lastPullStatus ?? '—'}
              </dd>
            </div>
          </dl>
          <ErrorMessage error={test.error ?? pull.error} />
          {test.data && <Notice tone="green">{test.data.detail}</Notice>}
          {pull.data && (
            <Notice tone="green">
              Read {number(pull.data.result.fetched)} lots.
              {pull.data.result.skipped.length > 0 && ` ${pull.data.result.skipped.length} skipped: ${pull.data.result.skipped[0]!.reason}.`}
            </Notice>
          )}
          <Notice>
            BrickLoop reads from Brick Owl and cannot change your Brick Owl store. Prices for each item come from Brick Owl's availability lookup, which Brick Owl only allows once it has approved your account for catalogue access, and is limited to 1,000 lookups a day.
          </Notice>

          {owner ? (
            <form
              className="space-y-3 border-t border-slate-100 pt-4"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate();
              }}
            >
              <p className="text-sm text-slate-600">
                Create an API key in your Brick Owl account under <em>Account settings → API</em>. It is encrypted when saved and never shown again.
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <Field label="API key" className="w-full max-w-md">
                  <Input type="password" autoComplete="new-password" value={apiKey} placeholder={status.configured ? 'unchanged' : ''} onChange={(e) => setApiKey(e.target.value)} />
                </Field>
                <Field label="Connection">
                  <Select value={mode} onChange={(e) => setMode(e.target.value as 'off' | 'read_only')}>
                    <option value="off">Off</option>
                    <option value="read_only">Read only</option>
                  </Select>
                </Field>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
              <ErrorMessage error={save.error} />
            </form>
          ) : (
            <p className="text-sm text-slate-500">Only the owner can change the connection.</p>
          )}
        </div>
      </Card>
      {status.listings.total > 0 && <BrickowlListings status={status} />}
    </>
  );
}

function BrickowlListings({ status }: { status: ChannelStatus }) {
  const [state, setState] = useState<'' | 'matched' | 'drift' | 'unmatched'>('');
  const [page, setPage] = useState(1);
  const list = useQuery({
    queryKey: ['listings-brickowl', state, page, status.listings],
    queryFn: () => api<Paged<Listing>>(`/api/v1/channels/brickowl/listings${qs({ state, page, pageSize: 25 })}`),
    placeholderData: (p) => p,
  });
  const n = status.listings;
  return (
    <Card
      title="What is on Brick Owl"
      actions={
        <Select aria-label="Show" className="w-48" value={state} onChange={(e) => { setState(e.target.value as typeof state); setPage(1); }}>
          <option value="">All ({number(n.total)})</option>
          <option value="unmatched">Not in BrickLoop ({number(n.unmatched)})</option>
          <option value="drift">Quantity or price differs ({number(n.drift)})</option>
          <option value="matched">Matches ({number(n.matched)})</option>
        </Select>
      }
    >
      <p className="mb-3 text-sm text-slate-600">
        A Brick Owl lot is matched to a BrickLoop lot only by an external ID that BrickLoop itself put on it, so lots you listed by hand on Brick Owl show as not in BrickLoop. There is no bring-in for them yet.
      </p>
      {list.isPending ? (
        <Spinner />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="data w-full min-w-[44rem] text-sm">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Colour</th>
                  <th>Cond.</th>
                  <th className="text-right">Qty here</th>
                  <th className="text-right">Qty BrickLoop</th>
                  <th className="text-right">Price here</th>
                  <th className="text-right">Price BrickLoop</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {list.data?.rows.map((l) => (
                  <tr key={l.id}>
                    <td>
                      {l.lotId ? <Link to={`/lots/${l.lotId}`} className="font-medium hover:underline">{l.itemNo}</Link> : <span className="font-medium">{l.itemNo}</span>}
                      <div className="max-w-48 truncate text-xs text-slate-500">{l.itemName}</div>
                    </td>
                    <td>{l.colorName ?? '—'}</td>
                    <td>{l.condition}</td>
                    <td className="text-right tabular-nums">{number(l.remoteQty)}</td>
                    <td className="text-right tabular-nums">{l.localQty === null ? '—' : number(l.localQty)}</td>
                    <td className="text-right tabular-nums">{unitMoney(l.remotePrice)}</td>
                    <td className="text-right tabular-nums">{unitMoney(l.localPrice)}</td>
                    <td>
                      <Badge tone={STATE_TONE[l.state]}>{STATE_LABEL[l.state]}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {list.data && <Pagination page={page} pageSize={25} total={list.data.total} onPage={setPage} />}
        </>
      )}
    </Card>
  );
}
